/**
 * FavoriteBooks — "Books That Define Me". Large covers, generous gaps,
 * reorderable by drag on the owner's own profile. Hover reveals the title
 * as an overlay instead of living permanently beneath the cover, so the
 * covers themselves stay the visual focus.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GripVertical } from 'lucide-react';
import { SectionHeader, cx } from '@/components/site/primitives';
import { fadeUp, stagger, viewport } from '@/lib/motion';
import { bookHref } from '@/lib/bookModel';
import BookCover from '@/components/books/BookCoverArt';
import EmptyState from './EmptyState';
import { PROFILE } from '@/constants/testIds';

export default function FavoriteBooks({ books, isOwnProfile, onReorder }) {
  const [dragId, setDragId] = useState(null);

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    onReorder?.(dragId, targetId);
    setDragId(null);
  };

  return (
    <section className="u-container mt-16 md:mt-24" data-testid={PROFILE.favorites}>
      <SectionHeader eyebrow="Defined by these" title="My Favorites" />

      {books.length === 0 ? (
        <EmptyState type="favorites" to="/app/bookshelf?shelf=shelf_favorites" compact className="mt-2" />
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-8 flex gap-6 overflow-x-auto pb-3 scrollbar-none"
        >
          {books.map((book) => (
            <motion.div
              key={book.id}
              variants={fadeUp}
              draggable={isOwnProfile}
              onDragStart={() => setDragId(book.id)}
              onDragOver={(e) => isOwnProfile && e.preventDefault()}
              onDrop={() => handleDrop(book.id)}
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className={cx('group relative w-36 shrink-0 sm:w-44', isOwnProfile && 'cursor-grab active:cursor-grabbing')}
            >
              <Link to={bookHref(book.id)}>
                <BookCover book={book} className="w-full shadow-lift" />
                <div className="pointer-events-none absolute inset-0 flex flex-col justify-end rounded-r-md rounded-l-[4px] bg-gradient-to-t from-black/70 via-black/0 to-black/0 p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <p className="font-display text-sm font-semibold leading-tight text-[#FFF7F0] text-balance">{book.title}</p>
                  <p className="mt-0.5 text-[11px] text-[#FFF7F0]/85">{book.author}</p>
                </div>
              </Link>
              {isOwnProfile && (
                <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-black/30 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100" aria-hidden="true">
                  <GripVertical className="h-3.5 w-3.5" />
                </span>
              )}
            </motion.div>
          ))}
        </motion.div>
      )}
    </section>
  );
}
